import { StackScreenProps } from "@react-navigation/stack";
import { InvoiceAddProductModalRootScreenParamList } from "../../../../types";
import { InvoiceArrivalItemForm } from "../../components";
import { useInvoiceArrivalContextProvider } from "../../utils/active-invoice-arrival";
import {
  Banner,
  Button,
  Page,
  Stack,
  useFlashMessages,
} from "@towersystems/roam-common-native";
import React from "react";
import { InvoiceArrivalItemFormValues } from "../../components/InvoiceArrivalItemForm/types";
import { useFormContext } from "react-hook-form";
import { useMutation } from "@apollo/client";
import { MUTATION_UPDATE_INVOICE_ARRIVAL_LINE_ITEMS } from "../../utils";
import {
  DataCoreBaseProduct,
  InvoiceArrival,
} from "@towersystems/roam-common/lib/generated-types";
import { Colors, View } from "react-native-ui-lib";
import { useDatabase } from "@nozbe/watermelondb/hooks";
import ProductModel from "../../../../utilities/data/model/Product";
import { Q } from "@nozbe/watermelondb";
import { useSelectedProductContextProvider } from "../../utils/selected-product/hooks";
import { useTranslation } from "../../../../utilities/i18n";
import { MatchingProductBanner } from "../../components/MatchingProductBanner";
import {
  dataCoreProductToFormInput,
  invoiceLineItemDataToGraphqlInput,
} from "../../utils/utils";
import { useQueryFindBaseProductByBarcodeAsync } from "../../../../utilities/retailer-data-core";
import { PlaceholderLoadingScreen } from "../../../products/screens/PlaceholderLoadingScreen";
import { ResourcePageSkeleton } from "../../../point-of-sale/screens/ResourcPageSkeleton";
import { MatchingBaseProductBanner } from "../../components/MatchingBaseProductBanner";
import { NonMatchingProductBanner } from "../../../../components/NonMatchingProductBanner";
import useOnline from "@towersystems/roam-common-native/lib/utilities/use-online";
export type AddNonProductModalScreenProps = StackScreenProps<
  InvoiceAddProductModalRootScreenParamList,
  "AddNonProductModalScreen"
> & {
  baseProduct?: DataCoreBaseProduct;
};

const AddNonProductModal = ({
  navigation,
  baseProduct,
}: AddNonProductModalScreenProps) => {
  const { t } = useTranslation("invoice_arrival");
  const database = useDatabase();

  const { handleSubmit, clearErrors, watch, trigger, setError, formState } =
    useFormContext<InvoiceArrivalItemFormValues>();

  const [existingProduct, setExistingProduct] = React.useState<
    ProductModel | undefined
  >(undefined);

  const { setProduct } = useSelectedProductContextProvider();

  const watchedStockNumber = watch("barcode");

  React.useEffect(() => {
    clearErrors("root");
    database
      .get<ProductModel>("products")
      .query(Q.where("stock_number", Q.eq(watchedStockNumber)))
      .then((products) => {
        if (products.length) {
          setExistingProduct(products[0]);
          setError("root.barcode", {
            type: "manual",
            message: t("error.barcode_exists"),
          });
        } else {
          setExistingProduct(undefined);
        }
      });
  }, [watchedStockNumber, setError]);

  const {
    errors,
    isDirty,
    isLoading,
    isSubmitting,
    isValid,
    isSubmitSuccessful,
  } = formState;
  console.log(watch(), errors, isValid);
  const [m] = useMutation<{ updateInvoiceArrivalLineItems: InvoiceArrival }>(
    MUTATION_UPDATE_INVOICE_ARRIVAL_LINE_ITEMS
  );

  const { invoiceArrival, setInvoiceArrival } =
    useInvoiceArrivalContextProvider();

  const flashMessagesContext = useFlashMessages();

  React.useEffect(() => {
    // to reset the form if the api returns error
    if (errors.root?.serverError) {
      trigger();
    }
  }, [errors.root?.serverError]);

  const handleOnSubmit = React.useCallback(
    async ({ ...input }: InvoiceArrivalItemFormValues) => {
      try {
        const result = await m({
          variables: {
            id: invoiceArrival.id,
            itemsToCreate: [
              {
                ...invoiceLineItemDataToGraphqlInput(input),
              },
            ],
          },
        });

        if (result.data?.updateInvoiceArrivalLineItems) {
          setInvoiceArrival(result.data?.updateInvoiceArrivalLineItems);
          navigation.getParent()?.goBack();
        } else {
          throw new Error(t("common:error"));
        }
      } catch (err: any) {
        flashMessagesContext.showMessage({
          message: err.message ?? t("common:error"),
          severity: "error",
        });
        setError("root.serverError", {
          type: "400",
          message: err.message ?? t("common:error"),
        });
      }
    },
    [invoiceArrival.id]
  );

  const matchingProductFoundMarkup = existingProduct ? (
    <MatchingProductBanner
      stockNumber={watchedStockNumber}
      onPress={async () => {
        setProduct(existingProduct);
        navigation.reset({
          index: 0,
          routes: [{ name: "AddProductModalScreen" }],
        });
      }}
    />
  ) : undefined;

  const nonMatchingMarkup = (
    <NonMatchingProductBanner
      onPress={async () => {
        navigation.navigate(
          "CreateProductRootScreen",
          //@ts-ignore
          {
            defaultValues: {
              stockNumber: watchedStockNumber,
              locationId: invoiceArrival.locationId,
            },
          }
        );
      }}
    />
  );

  const disabled =
    !isDirty ||
    isLoading ||
    isSubmitting ||
    !isValid ||
    isSubmitSuccessful ||
    !!existingProduct;

  const footerMarkup = (
    <Button
      disabled={disabled}
      label={t("button.add_product")}
      backgroundColor={Colors.action}
      color="white"
      onPress={handleSubmit(handleOnSubmit)}
    ></Button>
  );

  const baseProductFoundMarkup = baseProduct ? (
    <MatchingBaseProductBanner />
  ) : undefined;

  const banners =
    baseProductFoundMarkup ?? matchingProductFoundMarkup ?? nonMatchingMarkup;

  return (
    <Page footer={footerMarkup}>
      <Stack vertical>
        <View>{banners}</View>
        <InvoiceArrivalItemForm />
      </Stack>
    </Page>
  );
};

export const AddNonProductModalScreen = (
  props: AddNonProductModalScreenProps
) => {
  const online = useOnline();
  const { route } = props;
  const { barcode } = route.params;

  const [checkedBaseProduct, setCheckedBaseProduct] = React.useState<boolean>(
    online ? false : true
  );

  const [baseProduct, setBaseProduct] = React.useState<
    DataCoreBaseProduct | undefined
  >(undefined);

  const { query } = useQueryFindBaseProductByBarcodeAsync();

  React.useEffect(() => {
    if (false == online) {
      setCheckedBaseProduct(true);
      return;
    }
    if (!barcode) {
      setCheckedBaseProduct(false);
      return;
    }
    query(barcode)
      .then((baseProduct) => {
        setCheckedBaseProduct(true);
        setBaseProduct(baseProduct);
      })
      .catch(() => {
        setCheckedBaseProduct(false);
      })
      .finally(() => {
        setCheckedBaseProduct(true);
      });
  }, []);

  if (!checkedBaseProduct) {
    return (
      <PlaceholderLoadingScreen
        headerShown={false}
        content={ResourcePageSkeleton}
      />
    );
  }

  return (
    <InvoiceArrivalItemForm.Provider
      defaultFormValues={{
        ...dataCoreProductToFormInput(baseProduct),
      }}
    >
      <AddNonProductModal baseProduct={baseProduct} {...props} />
    </InvoiceArrivalItemForm.Provider>
  );
};
